import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faCalendarCheck,
  faChevronRight,
  faCircleInfo,
  faClock,
  faDownload,
  faFilter,
  faGear,
  faMoneyBillTransfer,
  faPlus,
  faReceipt,
  faRotate,
  faShieldHalved,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { useEntidadeContext } from "../EntidadeContext";
import { fluxopayApi } from "../services/fluxopayApi";
import FluxopayAnticipationPanel from "../components/fluxopay/FluxopayAnticipationPanel";
import FluxopayPayoutPanelSecure from "../components/fluxopay/FluxopayPayoutPanelSecure";
import FluxopayOperationalAlerts from "../components/fluxopay/FluxopayOperationalAlerts";
import TransactionProofModal from "../admin/components/TransactionProofModal";
import "./FluxopayPage.css";
import "./FluxopayProof.css";

import FluxopaySettingsPanel from "../components/fluxopay/FluxopaySettingsPanel";
import FluxopayDisputesPanel from "../components/fluxopay/FluxopayDisputesPanel";

import FluxopayNotificationCenter from "../components/fluxopay/FluxopayNotificationCenter";

import FluxopayRefundModal from "../components/fluxopay/FluxopayRefundModal";
import {fluxopayStatus,} from "../components/fluxopay/fluxopayStatus";

import FluxopayRiskPosition from "../components/fluxopay/FluxopayRiskPosition";
import FluxopayOnboardingPanel from "../components/fluxopay/FluxopayOnboardingPanel";

const money = (value) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const isValidCpfCnpj = (value) => {
  const document = String(value || "").replace(/\D/g, "");
  if (/^(\d)\1+$/.test(document)) return false;
  if (document.length === 11) {
    const digit = (length) => {
      let sum = 0;
      for (let index = 0; index < length; index += 1)
        sum += Number(document[index]) * (length + 1 - index);
      const result = (sum * 10) % 11;
      return result === 10 ? 0 : result;
    };
    return (
      digit(9) === Number(document[9]) && digit(10) === Number(document[10])
    );
  }
  if (document.length === 14) {
    const digit = (base, weights) => {
      const sum = weights.reduce(
        (total, weight, index) => total + Number(base[index]) * weight,
        0,
      );
      const remainder = sum % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };
    const base = document.slice(0, 12);
    const first = digit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const second = digit(
      `${base}${first}`,
      [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2],
    );
    return document.endsWith(`${first}${second}`);
  }
  return false;
};

let FLUXOPAY_ACCOUNT = {
  provider: "asaas",
  providerLabel: "Asaas",
  status: "not_started",
  walletId: "Nao criada",
  availableBalance: 0,
  processingBalance: 0,
  futureBalance: 0,
};

let FLUXOPAY_TRANSACTIONS = [];

function Status({ value }) {

  const {
    label,
    tone: color,
  } = fluxopayStatus(value);
  return (
    <span className={`fxpay-status fxpay-status--${color}`}>
      <i />
      {label}
    </span>
  );
}

function MetricCard({ icon, label, value, detail, tone = "navy" }) {
  return (
    <article className="fxpay-metric">
      <span className={`fxpay-metric__icon fxpay-tone--${tone}`}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

function Overview({ openTransaction }) {
  const total = FLUXOPAY_TRANSACTIONS.filter((x) => x.status !== "refunded").reduce(
    (sum, x) => sum + x.gross,
    0,
  );
  const platformFees = FLUXOPAY_TRANSACTIONS.reduce(
    (sum, x) => sum + x.platformFee,
    0,
  );
  const providerFees = FLUXOPAY_TRANSACTIONS.reduce(
    (sum, x) => sum + x.providerFee,
    0,
  );
  const net = FLUXOPAY_TRANSACTIONS.reduce((sum, x) => sum + x.net, 0);
  const pixTotal = FLUXOPAY_TRANSACTIONS.filter((x) => x.method === "PIX").reduce(
    (sum, x) => sum + x.gross,
    0,
  );
  const cardTotal = total - pixTotal;
  return (
    <>
      <section className="fxpay-metrics">
        <MetricCard
          icon={faWallet}
          label="Saldo disponível"
          value={money(FLUXOPAY_ACCOUNT.availableBalance)}
          detail="Pronto para transferir"
          tone="green"
        />
        <MetricCard
          icon={faClock}
          label="Em processamento"
          value={money(FLUXOPAY_ACCOUNT.processingBalance)}
          detail="Liquidações em andamento"
          tone="blue"
        />
        <MetricCard
          icon={faCalendarCheck}
          label="Agenda de recebíveis"
          value={money(FLUXOPAY_ACCOUNT.futureBalance)}
          detail="Próximos 30 dias"
          tone="purple"
        />
        <MetricCard
          icon={faArrowTrendUp}
          label="Recebido em agosto"
          value={money(total)}
          detail="+12,8% sobre julho"
          tone="navy"
        />
      </section>

      <section className="fxpay-grid fxpay-grid--overview">
        <article className="fxpay-card fxpay-chart-card">
          <header>
            <div>
              <small>VOLUME PROCESSADO</small>
              <h3>Recebimentos dos últimos 7 dias</h3>
            </div>
            <button className="fxpay-quiet">
              7 dias <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </header>
          <div className="fxpay-chart">
            {[42, 66, 51, 83, 62, 92, 73].map((height, index) => (
              <div key={index} className="fxpay-bar-column">
                <span style={{ height: `${height}%` }} />
                <small>
                  {["Sex", "Sáb", "Dom", "Seg", "Ter", "Qua", "Hoje"][index]}
                </small>
              </div>
            ))}
          </div>
          <div className="fxpay-chart-summary">
            <span>
              <i className="green" />
              Pix <b>{money(pixTotal)}</b>
            </span>
            <span>
              <i className="blue" />
              Cartão <b>{money(cardTotal)}</b>
            </span>
          </div>
        </article>

        <article className="fxpay-card fxpay-summary-card">
          <header>
            <div>
              <small>COMPOSIÇÃO</small>
              <h3>Resumo das taxas</h3>
            </div>
            <FontAwesomeIcon icon={faCircleInfo} />
          </header>
          <div className="fxpay-summary-row">
            <span>Volume bruto</span>
            <b>{money(total)}</b>
          </div>
          <div className="fxpay-summary-row">
            <span>Taxa Fluxopay</span>
            <b className="red">− {money(platformFees)}</b>
          </div>
          <div className="fxpay-summary-row">
            <span>Processamento</span>
            <b className="red">− {money(providerFees)}</b>
          </div>
          <div className="fxpay-summary-row total">
            <span>Líquido da clínica</span>
            <b>{money(net)}</b>
          </div>
          <p className="fxpay-note">
            <FontAwesomeIcon icon={faShieldHalved} /> O split é calculado no
            pagamento e conciliado pelo provedor.
          </p>
        </article>
      </section>

      <section className="fxpay-card fxpay-table-card">
        <header>
          <div>
            <small>MOVIMENTAÇÕES</small>
            <h3>Transações recentes</h3>
          </div>
          <button className="fxpay-link">
            Ver todas <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </header>
        <TransactionTable
          rows={FLUXOPAY_TRANSACTIONS.slice(0, 4)}
          openTransaction={openTransaction}
        />
      </section>
    </>
  );
}

function TransactionTable({ rows, openTransaction }) {
  return (
    <div className="fxpay-table-wrap">
      <table className="fxpay-table">
        <thead>
          <tr>
            <th>Transação</th>
            <th>Paciente / serviço</th>
            <th>Meio</th>
            <th>Valor bruto</th>
            <th>Taxas</th>
            <th>Líquido</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} onClick={() => openTransaction(row)}>
              <td>
                <b>{row.id}</b>
                <small>{row.date}</small>
              </td>
              <td>
                <b>{row.patient}</b>
                <small>{row.appointment}</small>
              </td>
              <td>
                <span
                  className={`fxpay-method fxpay-method--${row.method === "PIX" ? "pix" : "card"}`}
                >
                  {row.method === "PIX"
                    ? "Pix"
                    : `Cartão${row.installments > 1 ? ` · ${row.installments}x` : ""}`}
                </span>
              </td>
              <td>{money(row.gross)}</td>
              <td className="fxpay-negative">
                − {money(row.platformFee + row.providerFee)}
              </td>
              <td>
                <b>{money(row.net)}</b>
              </td>
              <td>
                <Status value={row.status} />
              </td>
              <td>
                <FontAwesomeIcon icon={faChevronRight} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Transactions({ openTransaction }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = useMemo(
    () =>
      FLUXOPAY_TRANSACTIONS.filter(
        (x) =>
          (status === "all" || x.status === status) &&
          `${x.patient} ${x.id} ${x.appointment}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [query, status],
  );
  return (
    <section className="fxpay-card fxpay-table-card">
      <header>
        <div>
          <small>HISTÓRICO</small>
          <h3>Todas as transações</h3>
        </div>
        <button className="fxpay-secondary">
          <FontAwesomeIcon icon={faDownload} /> Exportar
        </button>
      </header>
      <div className="fxpay-filters">
        <label>
          <FontAwesomeIcon icon={faFilter} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Paciente, serviço ou código"
          />
        </label>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos os status</option>
          <option value="available">Disponível</option>
          <option value="processing">Processando</option>
          <option value="future">A receber</option>
          <option value="refunded">Estornado</option>
        </select>
        <input type="date" defaultValue="2026-08-01" />
        <input type="date" defaultValue="2026-08-07" />
      </div>
      <TransactionTable rows={filtered} openTransaction={openTransaction} />
    </section>
  );
}

const fromCents = (value) => Number(value || 0) / 100;
const formatDate = (value) =>
  value ? new Date(value).toLocaleString("pt-BR") : "?";

function hydrateFluxopay(overview, transactions) {
  
  const account = overview.account;
  
  FLUXOPAY_ACCOUNT = {
    provider: account?.provider || "asaas",
    providerLabel: account?.provider || "Asaas",
    status: account?.status || "not_started",
    walletId: account?.provider_wallet_id || "Não criada",

    /*
    * Saldo efetivamente liberado após todos os controles.
    */
    availableBalance: fromCents(
      overview.balances?.usable_for_payout_cents
        ?? overview.balances?.available_cents
    ),

    /*
    * Saldo bruto informado pelo Asaas.
    */
    providerAvailableBalance: fromCents(
      overview.balances?.provider_available_cents
        ?? overview.balances?.available_cents
    ),

    processingBalance: fromCents(
      overview.balances?.processing_cents
    ),

    futureBalance: fromCents(
      overview.balances?.future_cents
    ),

    negativeBalance: fromCents(
      overview.balances?.negative_balance_cents
    ),

    refundReserve: fromCents(
      overview.balances?.refund_reserve_cents
    ),

    reservedPayout: fromCents(
      overview.balances?.reserved_payout_cents
    ),

    payoutsBlocked: Boolean(
      overview.balances?.payouts_blocked
    ),

    riskStatus:
      overview.balances?.risk_status || "normal",

    reserveActive: Boolean(
      overview.balances?.reserve_active
    ),

    reservePercentage: Number(
      overview.balances?.reserve_percentage || 0
    ),

    reserveUntil:
      overview.balances?.reserve_until || null,
  };
  
  FLUXOPAY_TRANSACTIONS = (transactions.data || []).map((transaction) => {
    const order = transaction.order || {};
    return {
      transactionId: transaction.id,
      id:
        order.external_id ||
        transaction.provider_transaction_id ||
        `FXP-${transaction.id}`,
      patient: order.paciente?.nome || "Paciente nao informado",
      appointment:
        order.metadata?.description ||
        (order.agendamento_id
          ? `Agendamento #${order.agendamento_id}`
          : "Cobranca avulsa"),
      method: order.payment_method === "pix" ? "PIX" : "CREDIT_CARD",
      installments: order.installments || 1,
      gross: fromCents(order.gross_amount_cents || transaction.amount_cents),
      refunded: fromCents(order.refunded_amount_cents),
      refundStatus: order.refund_status || null,
      platformFee: fromCents(order.platform_fee_cents),
      providerFee: fromCents(order.provider_fee_cents),
      net: fromCents(order.clinic_net_cents),
      financialsConfirmed: Boolean(order.reconciled_at),
      financialError: order.reconciliation_error || null,
      status: transaction.status === "paid" ? "available" : transaction.status,
      date: formatDate(transaction.created_at),
      availableAt: formatDate(order.provider_credit_at || order.expires_at),
      uuid: order.uuid,
      provider: transaction.provider || order.provider,
    };
  });
  
}

function ActivationModal({ entity, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    email: entity?.email || "",
    mobile_phone: entity?.telefone || "",
    income_value: "",
    company_type: "LIMITED",
    address: entity?.endereco || "",
    address_number: entity?.numero || "",
    province: entity?.bairro || "",
    postal_code: entity?.cep || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const change = (e) =>
    setForm((v) => ({ ...v, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await fluxopayApi.requestAsaasActivation({
        ...form,
        income_value: Number(form.income_value),
      });
      await onSubmitted();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          Object.values(err.response?.data?.errors || {})[0]?.[0] ||
          "Não foi possível enviar a solicitação.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fxpay-modal-backdrop" onMouseDown={onClose}>
      <form
        className="fxpay-activation-modal"
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <small>ATIVAÇÃO FLUXOPAY</small>
            <h2>Dados empresariais</h2>
            <p>Revise os dados que serão enviados para análise.</p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </header>
        {error && <p className="fxpay-warning">{error}</p>}
        <div className="fxpay-form-grid">
          <label>
            E-mail
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={change}
              required
            />
          </label>
          <label>
            Celular
            <input
              name="mobile_phone"
              value={form.mobile_phone}
              onChange={change}
              required
            />
          </label>
          <label>
            Faturamento mensal estimado
            <input
              name="income_value"
              type="number"
              min="1"
              step="0.01"
              value={form.income_value}
              onChange={change}
              required
            />
          </label>
          <label>
            Tipo empresarial
            <select
              name="company_type"
              value={form.company_type}
              onChange={change}
            >
              <option value="MEI">MEI</option>
              <option value="LIMITED">Sociedade limitada</option>
              <option value="INDIVIDUAL">Empresário individual</option>
              <option value="ASSOCIATION">Associação</option>
            </select>
          </label>
          <label className="wide">
            Endereço
            <input
              name="address"
              value={form.address}
              onChange={change}
              required
            />
          </label>
          <label>
            Número
            <input
              name="address_number"
              value={form.address_number}
              onChange={change}
              required
            />
          </label>
          <label>
            Bairro
            <input
              name="province"
              value={form.province}
              onChange={change}
              required
            />
          </label>
          <label>
            CEP
            <input
              name="postal_code"
              value={form.postal_code}
              onChange={change}
              required
            />
          </label>
        </div>
        <p className="fxpay-note">
          <FontAwesomeIcon icon={faShieldHalved} /> Nenhum dado bancário ou de
          cartão é solicitado nesta etapa.
        </p>
        <footer>
          <button type="button" className="fxpay-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="fxpay-primary" disabled={saving}>
            {saving ? "Enviando..." : "Solicitar ativação"}
          </button>
        </footer>
      </form>
    </div>
  );
}
function ChargeModal({ onClose, onSubmitted }) {
  const emptyForm = {
    patient_id: "",
    amount: "120,00",
    payment_method: "credit_card",
    installments: "1",
    description: "Pagamento de atendimento",
    name: "",
    cpf_cnpj: "",
    email: "",
    phone: "",
    address: "",
    address_number: "",
    complement: "",
    province: "",
    postal_code: "",
    city: "",
    state: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const change = (e) =>
    setForm((value) => ({ ...value, [e.target.name]: e.target.value }));
  const loadPatient = async () => {
    if (!form.patient_id) return;
    setLoadingPatient(true);
    setError("");
    try {
      const response = await fluxopayApi.patient(form.patient_id);
      const patient = response.paciente || response;
      setForm((value) => ({
        ...value,
        name: patient.nome || "",
        cpf_cnpj: patient.cpf || "",
        email: patient.email || "",
        phone: `${patient.dddcelular || ""}${patient.celular || ""}`,
        address: patient.endereco || "",
        address_number: patient.numero || "",
        complement: patient.complemento || "",
        province: patient.bairro || "",
        postal_code: patient.cep || "",
        city: patient.cidade || "",
        state: patient.estado || "",
      }));
    } catch (err) {
      setError(
        err.response?.data?.message || "Paciente não encontrado nesta clínica.",
      );
    } finally {
      setLoadingPatient(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(
      String(form.amount).replace(/\./g, "").replace(",", "."),
    );
    if (!Number.isFinite(amount) || amount < 1) {
      setError("Informe um valor válido.");
      return;
    }
    if (!isValidCpfCnpj(form.cpf_cnpj)) {
      setError("Informe um CPF ou CNPJ válido para o pagador.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const order = await fluxopayApi.createAsaasOrder({
        paciente_id: Number(form.patient_id),
        payment_method: form.payment_method,
        installments: Number(form.installments || 1),
        gross_amount_cents: Math.round(amount * 100),
        idempotency_key: `web-${Date.now()}`,
        metadata: { description: form.description },
        customer_data: {
          name: form.name,
          cpf_cnpj: form.cpf_cnpj,
          email: form.email,
          phone: form.phone,
          address: form.address,
          address_number: form.address_number,
          complement: form.complement,
          province: form.province,
          postal_code: form.postal_code,
          city: form.city,
          state: form.state,
        },
      });
      const checkout = order.metadata?.checkout_url;
      const pix = order.metadata?.pix?.copy_paste;
      if (checkout) window.open(checkout, "_blank", "noopener,noreferrer");
      if (pix) {
        await navigator.clipboard?.writeText(pix);
        window.alert(
          "Código Pix gerado e copiado para a área de transferência.",
        );
      }
      await onSubmitted();
      onClose();
    } catch (err) {
      const providerErrors = err.response?.data?.errors;
      if (Array.isArray(providerErrors)) {
        setError(
          providerErrors
            .map((item) => item.description || item.message)
            .filter(Boolean)
            .join(" "),
        );
      } else {
        setError(
          err.response?.data?.message ||
            Object.values(providerErrors || {})[0]?.[0] ||
            "Não foi possível criar a cobrança Asaas.",
        );
      }
    } finally {
      setSaving(false);
    }
  };
  const card = form.payment_method === "credit_card";
  return (
    <div className="fxpay-modal-backdrop" onMouseDown={onClose}>
      <form
        className="fxpay-activation-modal"
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <small>NOVA COBRANÇA</small>
            <h2>Dados da cobrança e do pagador</h2>
            <p>
              Os dados do cartão serão informados somente no ambiente seguro do
              Asaas.
            </p>
          </div>
          <button type="button" onClick={onClose}>
            ×
          </button>
        </header>
        {error && <p className="fxpay-warning">{error}</p>}
        <div className="fxpay-form-grid">
          <label>
            ID do paciente
            <input
              name="patient_id"
              type="number"
              min="1"
              value={form.patient_id}
              onChange={change}
              onBlur={loadPatient}
              required
            />
          </label>
          <label>
            Valor da cobrança
            <input
              name="amount"
              value={form.amount}
              onChange={change}
              required
            />
          </label>
          <label>
            Forma de pagamento
            <select
              name="payment_method"
              value={form.payment_method}
              onChange={change}
            >
              <option value="credit_card">Cartão de crédito</option>
              <option value="pix">Pix</option>
            </select>
          </label>
          {card && (
            <label>
              Parcelas
              <select
                name="installments"
                value={form.installments}
                onChange={change}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1}x
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="wide">
            Descrição
            <input
              name="description"
              value={form.description}
              onChange={change}
              required
            />
          </label>
          <label className="wide">
            Nome do pagador
            <input
              name="name"
              value={form.name}
              onChange={change}
              required
              disabled={loadingPatient}
            />
          </label>
          <label>
            CPF/CNPJ
            <input
              name="cpf_cnpj"
              value={form.cpf_cnpj}
              onChange={change}
              required
            />
          </label>
          <label>
            Telefone
            <input name="phone" value={form.phone} onChange={change} required />
          </label>
          <label className="wide">
            E-mail
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={change}
            />
          </label>
          {card && (
            <>
              <label className="wide">
                Endereço
                <input
                  name="address"
                  value={form.address}
                  onChange={change}
                  required
                />
              </label>
              <label>
                Número
                <input
                  name="address_number"
                  value={form.address_number}
                  onChange={change}
                  required
                />
              </label>
              <label>
                Complemento
                <input
                  name="complement"
                  value={form.complement}
                  onChange={change}
                />
              </label>
              <label>
                Bairro
                <input
                  name="province"
                  value={form.province}
                  onChange={change}
                  required
                />
              </label>
              <label>
                CEP
                <input
                  name="postal_code"
                  value={form.postal_code}
                  onChange={change}
                  required
                />
              </label>
              <label>
                Cidade
                <input name="city" value={form.city} onChange={change} />
              </label>
              <label>
                UF
                <input
                  name="state"
                  maxLength="2"
                  value={form.state}
                  onChange={change}
                />
              </label>
            </>
          )}
        </div>
        <p className="fxpay-note">
          <FontAwesomeIcon icon={faShieldHalved} /> Ao sair do campo ID, os
          dados existentes do paciente são carregados automaticamente.
        </p>
        <footer>
          <button type="button" className="fxpay-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button className="fxpay-primary" disabled={saving || loadingPatient}>
            {saving
              ? "Gerando cobrança..."
              : loadingPatient
                ? "Carregando paciente..."
                : "Continuar para pagamento"}
          </button>
        </footer>
      </form>
    </div>
  );
}
export default function FluxopayPage() {
  const { currentEntidade } = useEntidadeContext();
  const [tab, setTab] = useState("overview");
  const [selected, setSelected] = useState(null);
  const [refundTransaction, setRefundTransaction] = useState(null);
  const [transactionProof, setTransactionProof] = useState(null);
  const [loadingProof, setLoadingProof] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const [accountStatus, setAccountStatus] = useState("not_started");
  const [showActivation, setShowActivation] = useState(false);
  const [showCharge, setShowCharge] = useState(false);
  const [syncingOrder, setSyncingOrder] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [overview, transactions] = await Promise.all([
        fluxopayApi.overview(),
        fluxopayApi.transactions({ per_page: 100 }),
      ]);

      hydrateFluxopay(overview, transactions);

      setAccountStatus(overview.onboarding_status || "not_started");
      setVersion((value) => value + 1);
    } catch (e) {
      setError(
        e.response?.data?.message || "Nao foi possivel carregar o Fluxopay.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const activate = () => setShowActivation(true);
  const createCharge = () => setShowCharge(true);
  const openTransactionProof = async () => {
    if (!selected?.transactionId) return;
    setLoadingProof(true); setError("");
    try {
      setTransactionProof(await fluxopayApi.transactionProof(selected.transactionId));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "N\u00e3o foi poss\u00edvel carregar a comprova\u00e7\u00e3o da transa\u00e7\u00e3o.");
    } finally { setLoadingProof(false); }
  };
  const syncSelectedOrder = async () => {
    if (!selected?.uuid) return;
    setSyncingOrder(true);
    setError("");
    try {
      const result = await fluxopayApi.syncAsaasOrder(selected.uuid);
      await load();
      setSelected(null);
      window.alert(
        result.payment_found
          ? "Status no Asaas: " + result.provider_status + "."
          : "O Checkout foi criado, mas nenhuma cobrança financeira foi localizada na subconta Asaas para esta sessão.",
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Não foi possível consultar o Checkout no Asaas.",
      );
    } finally {
      setSyncingOrder(false);
    }
  };
  if (loading && version === 0)
    return (
      <main className="fxpay-page">
        <section className="fxpay-card">Carregando Fluxopay...</section>
      </main>
    );
  const tabs = [
    ["overview", "Visão geral", faWallet],
    ["transactions", "Transações", faReceipt],
    ["disputes", "Contestações", faShieldHalved],
    ["receivables", "A receber", faCalendarCheck],
    ["payouts", "Repasses", faMoneyBillTransfer],
    ["settings", "Configuração", faGear],
  ];
  return (
    <main className="fxpay-page">
      <FluxopayOperationalAlerts />
      <section className="fxpay-hero">
        <div>
          <span className="fxpay-brand">
            <i>
              <FontAwesomeIcon icon={faWallet} />
            </i>
            Fluxopay
          </span>
          <h1>Recebimentos simples. Gestão integrada.</h1>
          <p>
            Acompanhe pagamentos dos agendamentos, taxas, recebíveis e repasses
            em um só lugar.
          </p>
          <div className="fxpay-provider">
            <i />
            <b>
              {accountStatus === "active"
                ? "Conta ativa"
                : accountStatus === "pending_review"
                  ? "Solicitacao em analise"
                  : "Conta nao ativada"}
            </b>
            <span>Provedor: {FLUXOPAY_ACCOUNT.providerLabel}</span>
            <span>Asaas</span>
          </div>
        </div>
        <div className="fxpay-hero-actions">
          <FluxopayNotificationCenter />
          <button
            className="fxpay-icon-button"
            title="Atualizar"
            onClick={load}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faRotate} />
          </button>
          {accountStatus === "active" ? (
            <button className="fxpay-primary" onClick={createCharge}>
              <FontAwesomeIcon icon={faPlus} /> Nova cobrança
            </button>
          ) : (
            <button
              className="fxpay-primary"
              onClick={activate}
              disabled={
                accountStatus === "pending_review" ||
                accountStatus === "under_review"
              }
            >
              {accountStatus === "pending_review"
                ? "Aguardando analise"
                : accountStatus === "under_review"
                  ? "Cadastro em analise"
                  : "Solicitar ativacao"}
            </button>
          )}
        </div>
      </section>
      {error && <p className="fxpay-warning">{error}</p>}
      <FluxopayRiskPosition
        account={FLUXOPAY_ACCOUNT}
      />
      <nav className="fxpay-tabs">
        {tabs.map(([key, label, icon]) => (
          <button
            key={key}
            className={tab === key ? "active" : ""}
            onClick={() => setTab(key)}
          >
            <FontAwesomeIcon icon={icon} />
            {label}
          </button>
        ))}
      </nav>
      <div className="fxpay-content">
        {tab === "overview" && <Overview openTransaction={setSelected} />}{" "}
        {tab === "transactions" && (<Transactions openTransaction={setSelected} />)}{" "}
        {tab === "disputes" && (<FluxopayDisputesPanel />)}{" "}
        
        {tab === "receivables" && (
          accountStatus === "active" ? (
            <FluxopayAnticipationPanel />
          ) : ["under_review", "pending_documents", "rejected"].includes(
              accountStatus
            ) ? (
            <FluxopayOnboardingPanel onUpdated={load} />
          ) : (
            <section className="fxpay-card">
              <small>A RECEBER</small>
              <h3>Funcionalidade ainda indisponível</h3>

              <p className="fxpay-note">
                {accountStatus === "pending_review"
                  ? "A solicitação precisa ser aprovada e a subconta criada antes de consultar os recebíveis."
                  : "Ative o FluxoPay para consultar recebíveis e antecipações."}
              </p>

              {accountStatus === "not_started" && (
                <button
                  type="button"
                  className="fxpay-primary"
                  onClick={activate}
                >
                  Solicitar ativação
                </button>
              )}
            </section>
          )
        )}{" "}
        {tab === "payouts" && (<FluxopayPayoutPanelSecure />)}{" "}
        {tab === "settings" && (
          accountStatus === "active" ? (
            <FluxopaySettingsPanel
              entityName={
                currentEntidade?.nome || "Clínica demonstração"
              }
            />
          ) : ["under_review", "pending_documents", "rejected"].includes(
              accountStatus
            ) ? (
            <FluxopayOnboardingPanel onUpdated={load} />
          ) : (
            <section className="fxpay-card">
              <small>FLUXOPAY</small>

              <h3>
                {accountStatus === "pending_review"
                  ? "Solicitação aguardando aprovação"
                  : "Ative sua conta para continuar"}
              </h3>

              <p className="fxpay-note">
                {accountStatus === "pending_review"
                  ? "A equipe FluxoPay ainda precisa revisar os dados e criar a subconta no Asaas."
                  : "Solicite a ativação para iniciar a análise cadastral no Asaas."}
              </p>

              {accountStatus === "not_started" && (
                <button
                  type="button"
                  className="fxpay-primary"
                  onClick={activate}
                >
                  Solicitar ativação
                </button>
              )}
            </section>
          )
        )}
      </div>
      {showActivation && (
        <ActivationModal
          entity={currentEntidade}
          onClose={() => setShowActivation(false)}
          onSubmitted={load}
        />
      )}
      {showCharge && (
        <ChargeModal onClose={() => setShowCharge(false)} onSubmitted={load} />
      )}
      {selected && (
        <div
          className="fxpay-modal-backdrop"
          onMouseDown={() => setSelected(null)}
        >
          <aside
            className="fxpay-drawer"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header>
              <div>
                <small>DETALHES DA TRANSAÇÃO</small>
                <h2>{selected.id}</h2>
              </div>
              <button onClick={() => setSelected(null)}>×</button>
            </header>
            <Status value={selected.status} />
            <div className="fxpay-drawer-patient">
              <span>{selected.patient.charAt(0)}</span>
              <div>
                <b>{selected.patient}</b>
                <small>{selected.appointment}</small>
              </div>
            </div>
            <div className="fxpay-detail">
              <span>Pagamento</span>
              <b>
                {selected.method === "PIX"
                  ? "Pix"
                  : `Cartão de crédito · ${selected.installments || 1}x`}
              </b>
            </div>
            <div className="fxpay-detail">
              <span>Data</span>
              <b>{selected.date}</b>
            </div>
            <div className="fxpay-detail">
              <span>Disponibilidade</span>
              <b>{selected.availableAt}</b>
            </div>
            <hr />
            <div className="fxpay-detail">
              <span>Valor bruto</span>
              <b>{money(selected.gross)}</b>
            </div>
            <div className="fxpay-detail">
              <span>Taxa Fluxopay</span>
              <b className="red">− {money(selected.platformFee)}</b>
            </div>
            <div className="fxpay-detail">
              <span>
                Taxa do provedor
                {selected.financialsConfirmed
                  ? ""
                  : " (estimada)"}
              </span>
              <b className="red">− {money(selected.providerFee)}</b>
            </div>
            <div className="fxpay-detail total">
              <span>
                {selected.financialsConfirmed
                  ? "Líquido confirmado pelo Asaas"
                  : "Líquido estimado"}
              </span>

              <b>{money(selected.net)}</b>
            </div>

            {!selected.financialsConfirmed && (
              <p className="fxpay-warning">
                Aguardando a apuração financeira do provedor.
                A taxa e o líquido ainda podem ser atualizados.
              </p>
            )}
            {selected.provider === "asaas" &&
              ["pending", "processing"].includes(selected.status) && (
                <button
                  className="fxpay-primary"
                  onClick={syncSelectedOrder}
                  disabled={syncingOrder}
                >
                  <FontAwesomeIcon icon={faRotate} />
                  {syncingOrder
                    ? "Consultando Asaas..."
                    : "Consultar status no Asaas"}
                </button>
              )}
            {selected.uuid &&
            ["available", "partially_refunded"].includes(
              selected.status
            ) &&
            selected.gross - selected.refunded > 0 && (
              <button
                className="fxpay-secondary"
                type="button"
                onClick={() => setRefundTransaction(selected)}
              >
                Solicitar estorno
              </button>
            )}  
            <button className="fxpay-secondary fxpay-proof-button" type="button" onClick={openTransactionProof} disabled={loadingProof}>
              <FontAwesomeIcon icon={faShieldHalved} />
              {loadingProof ? "Carregando..." : "Comprova\u00e7\u00e3o da transa\u00e7\u00e3o"}
            </button>
          </aside>
        </div>
      )}
      {refundTransaction && (
        <FluxopayRefundModal
          transaction={refundTransaction}
          onClose={() => setRefundTransaction(null)}
          onCompleted={async () => {
            await load();
            setSelected(null);
          }}
        />
      )}
      {transactionProof && <TransactionProofModal data={transactionProof} onClose={() => setTransactionProof(null)} contractRequest={fluxopayApi.transactionContract} />}
    </main>
  );
}
